import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ViewColumnsIcon,
  XMarkIcon,
  CheckIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { toast } from 'react-toastify';
import { usePageReset } from '../../helpers/usePageReset';
import { Pagination } from '../../components/Pagination';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { OverlayGrayBackground } from '../../components/OverlayGrayBackground';
import CSVExportDropdown from '../../components/reports/CSVExportDropdown';
import OrderDetailsDrawer from './OrderDetailsDrawer';
import ColumnsPanel from './ColumnsPanel';
import StatusFilterPanel from './StatusFilterPanel';
import CustomDateCalendar from './CustomDateCalendar';
import OrdersCardList from './OrdersCardList';
import {
  ALL_COLUMNS,
  ALL_COLUMN_KEYS,
  DEFAULT_VIEW_COLUMNS,
  VIEW_PRESETS,
  RIGHT_ALIGNED_COLUMNS,
} from './ordersColumns';
import {
  formatCurrency,
  formatOrderDate,
  computeMargin,
  marginToneClass,
  getShopifyOrderUrl,
  getAmazonOrderUrl,
  STATUS_DOT_COLORS,
} from './ordersHelpers';

// Channel options for filtering
const CHANNEL_OPTIONS = ['Shopify', 'Amazon'];

const AMAZON_STATUSES = [
  'PendingAvailability',
  'Pending',
  'Unshipped',
  'PartiallyShipped',
  'Shipped',
  'InvoiceUnconfirmed',
  'Canceled',
  'Unfulfillable',
];

const SHOPIFY_STATUSES = [
  'AUTHORIZED',
  'EXPIRED',
  'PAID',
  'PARTIALLY_PAID',
  'PARTIALLY_REFUNDED',
  'PENDING',
  'REFUNDED',
  'VOIDED',
];

const STATUS_GROUPS = [
  { label: 'Amazon', statuses: AMAZON_STATUSES },
  { label: 'Shopify', statuses: SHOPIFY_STATUSES },
];

const STATUS_VALUES = [...AMAZON_STATUSES, ...SHOPIFY_STATUSES];

const DATE_PRESETS = [
  { key: 'all', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last14', label: 'Last 14 days' },
  { key: 'last30', label: 'Last 30 days' },
];

const SORTABLE_COLUMNS = {
  date_of_order: 'date_of_order',
  external_order_id: 'external_order_id',
  channel: 'channel',
  order_status: 'order_status',
  customer: 'customer_email',
  gross_sales: 'gross_sales',
  discounts: 'discounts',
  taxes: 'taxes',
  shipping_charges: 'shipping_charges',
  refund_total: 'refund_total',
  total_paid: 'total_paid',
  earliest_ship_date: 'earliest_ship_date',
  latest_ship_date: 'latest_ship_date',
};

const getPresetDates = (presetKey) => {
  const today = format(new Date(), 'yyyy-MM-dd');
  switch (presetKey) {
    case 'all':
      return { startDate: '', endDate: '' };
    case 'today':
      return { startDate: today, endDate: today };
    case 'last7':
      return { startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'), endDate: today };
    case 'last14':
      return { startDate: format(subDays(new Date(), 14), 'yyyy-MM-dd'), endDate: today };
    case 'last30':
      return { startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'), endDate: today };
    default:
      return null;
  }
};

const derivePreset = (startDate, endDate) => {
  if (!startDate && !endDate) return 'all';
  for (const preset of ['today', 'last7', 'last14', 'last30']) {
    const dates = getPresetDates(preset);
    if (dates.startDate === startDate && dates.endDate === endDate) return preset;
  }
  return 'custom';
};

const formatShortRangeDate = (isoDate) => {
  if (!isoDate) return '';
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const COLUMNS_STORAGE_KEY = 'ordersVisibleColumns';
const SAVED_VIEWS_STORAGE_KEY = 'ordersSavedViews';
const ACTIVE_VIEW_STORAGE_KEY = 'ordersActiveViewId';

const loadStoredColumns = () => {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((k) => ALL_COLUMN_KEYS.includes(k))) {
      return parsed;
    }
  } catch {
    // ignore malformed storage
  }
  return null;
};

const loadSavedViews = () => {
  try {
    const raw = localStorage.getItem(SAVED_VIEWS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter(
          (v) =>
            v &&
            (typeof v.name === 'string' || typeof v.label === 'string') &&
            Array.isArray(v.columns)
        )
        .map((v) => ({
          id: v.id || `custom-${(v.name || v.label).replace(/\s+/g, '-').toLowerCase()}`,
          name: v.name || v.label,
          columns: v.columns,
        }));
    }
  } catch {
    // ignore malformed storage
  }
  return [];
};

const loadActiveViewId = () => {
  try {
    return localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY) || 'default';
  } catch {
    return 'default';
  }
};

const ChannelPill = ({ channel, externalOrderId }) => {
  if (!channel) return <span className="text-gray-400">&mdash;</span>;
  const isShopify = channel === 'Shopify';
  const pill = (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${
        isShopify ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
      }`}
    >
      {channel}
    </span>
  );
  if (!externalOrderId) return pill;
  const url = isShopify ? getShopifyOrderUrl(externalOrderId) : getAmazonOrderUrl(externalOrderId);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:opacity-80"
      onClick={(e) => e.stopPropagation()}
    >
      {pill}
    </a>
  );
};

const StatusCell = ({ status, channel }) => {
  if (!status) return <span className="text-gray-300">&mdash;</span>;
  const colors = STATUS_DOT_COLORS(status, channel);
  const [dotColor, textColor] = colors.split(' ');
  return (
    <span className={`inline-flex items-center gap-1.5 text-[12.5px] font-medium ${textColor}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
      {status}
    </span>
  );
};

const ItemsCell = ({ order }) => {
  const items = order.OrderLineItems || [];
  if (items.length === 0) return <span className="text-gray-300">&mdash;</span>;
  const first = items[0];
  return (
    <div className="flex items-center gap-2 min-w-0">
      {first.Product?.image ? (
        <img
          src={first.Product.image}
          alt=""
          className="h-6 w-6 object-contain rounded flex-shrink-0"
          loading="lazy"
        />
      ) : (
        <div className="h-6 w-6 rounded bg-gray-100 flex-shrink-0" />
      )}
      <span className="truncate text-sm text-gray-900 max-w-[220px]">
        {first.Product?.name || first.sku || '-'}
      </span>
      {items.length > 1 && (
        <span className="text-[11px] text-gray-400 flex-shrink-0">+{items.length - 1}</span>
      )}
    </div>
  );
};

const MoneyCell = ({ value, negativeRed = false, bold = false, dashZero = false }) => {
  const num = Number(value);
  if (dashZero && (!value || isNaN(num) || num === 0)) {
    return <span className="text-gray-300">&mdash;</span>;
  }
  const formatted = formatCurrency(value);
  if (formatted === null) return <span className="text-gray-300">&mdash;</span>;
  const isNegativeStyle = negativeRed && !isNaN(num) && num > 0;
  return (
    <span
      className={`tabular-nums ${bold ? 'font-medium text-gray-900' : 'text-gray-700'} ${
        isNegativeStyle ? '!text-red-600' : ''
      }`}
    >
      {isNegativeStyle ? `\u2212${formatted}` : formatted}
    </span>
  );
};

// Toolbar filter pill with attached popover. Active pills render dark with an inline clear button.
const FilterPill = ({ label, active, onClear, open, setOpen, children, popoverClassName = '' }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen]);

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex h-8 flex-shrink-0 items-center gap-1 rounded-md border px-2.5 text-[12px] transition-colors ${
          active
            ? 'border-transparent bg-gray-900 font-medium text-white'
            : 'border-gray-300 bg-white text-gray-500 hover:border-gray-400 hover:text-gray-900'
        }`}
      >
        {label}
        {active && onClear ? (
          <span
            role="button"
            aria-label={`Clear ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="-mr-0.5 rounded-full p-0.5 hover:bg-white/20"
          >
            <XMarkIcon className="h-3 w-3" />
          </span>
        ) : (
          <ChevronDownIcon className="h-3 w-3 opacity-60" />
        )}
      </button>
      {open && (
        <div
          className={`absolute left-0 mt-1 rounded-md border border-gray-200 bg-white shadow-lg z-40 ${popoverClassName}`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const Orders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const getArrayParam = (key, def) => {
    const values = searchParams.getAll(key);
    return values.length ? values : def;
  };
  const getValueParam = (key, def) => searchParams.get(key) || def;
  const getNumberParam = (key, def) => {
    const num = parseInt(searchParams.get(key), 10);
    return isNaN(num) ? def : num;
  };

  const [orders, setOrders] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState(() => getArrayParam('channel', ['Shopify', 'Amazon']));
  const [selectedStatuses, setSelectedStatuses] = useState(() => getArrayParam('statuses', [...STATUS_VALUES]));
  const [searchTerm, setSearchTerm] = useState(() => getValueParam('search', ''));
  const [searchQuery, setSearchQuery] = useState(() => getValueParam('search', ''));
  const defaultEndDate = format(new Date(), 'yyyy-MM-dd');
  const defaultStartDate = format(subDays(new Date(), 1), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(() => getValueParam('startDate', defaultStartDate));
  const [endDate, setEndDate] = useState(() => getValueParam('endDate', defaultEndDate));
  const [dateField, setDateField] = useState(() => getValueParam('dateField', 'date_of_order'));
  const [sortKey, setSortKey] = useState(() => {
    const key = getValueParam('sortKey', 'date_of_order');
    return SORTABLE_COLUMNS[key] ? key : 'date_of_order';
  });
  const [sortDir, setSortDir] = useState(() => (getValueParam('sortDir', 'desc') === 'asc' ? 'asc' : 'desc'));
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(() => getNumberParam('page', 1));
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(() => getNumberParam('itemsPerPage', 50));

  const [visibleColumns, setVisibleColumns] = useState(
    () => loadStoredColumns() || [...DEFAULT_VIEW_COLUMNS]
  );
  const [savedViews, setSavedViews] = useState(() => loadSavedViews());
  const [activeViewId, setActiveViewId] = useState(() => loadActiveViewId());
  const [showColumnsPanel, setShowColumnsPanel] = useState(false);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [showChannelPopover, setShowChannelPopover] = useState(false);
  const [showStatusPopover, setShowStatusPopover] = useState(false);
  const [showDatePopover, setShowDatePopover] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [detailsOrderId, setDetailsOrderId] = useState(null);

  const viewDropdownRef = useRef(null);
  const calendarRef = useRef(null);

  const datePreset = derivePreset(startDate, endDate);

  // Auto-reset page to 1 when non-page params change
  const shouldSkipRequest = usePageReset(setCurrentPage, currentPage, 'get:/api/v1/orders');

  const [pagination, setPagination] = useState({ rangeRows: '', totalPages: 0, isTheLastPage: false });
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoadComplete, setIsFirstLoadComplete] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns));
    } catch {
      // ignore storage failures
    }
  }, [visibleColumns]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, activeViewId);
    } catch {
      // ignore storage failures
    }
  }, [activeViewId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (viewDropdownRef.current && !viewDropdownRef.current.contains(event.target)) {
        setShowViewDropdown(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buildRequestParams = () => {
    const params = new URLSearchParams();
    if (dateField !== 'date_of_order') {
      params.append('amazonShipping', 'true');
      params.append('amazonDateField', dateField);
      params.append('channel', 'Amazon');
    } else if (selectedChannels.length > 0 && selectedChannels.length < CHANNEL_OPTIONS.length) {
      selectedChannels.forEach((channel) => params.append('channel', channel));
    }
    if (selectedStatuses.length > 0 && selectedStatuses.length < STATUS_VALUES.length) {
      selectedStatuses.forEach((status) => params.append('statuses', status));
    }
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (searchQuery) params.append('search', searchQuery);
    return params;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchOrders = async (showLoader = false) => {
      if (isMounted && showLoader) {
        setIsLoading(true);
      }
      try {
        const params = buildRequestParams();
        params.append('page', currentPage);
        params.append('itemsPerPage', currentItemsPerPage);
        params.append('sortKey', SORTABLE_COLUMNS[sortKey] || 'date_of_order');
        params.append('sortDir', sortDir);

        const { data } = await axios.get(`/api/v1/orders?${params.toString()}`);
        if (isMounted) {
          setOrders(data.orders || []);
          setTotalRecords(data.total || 0);
          const totalPages = Math.ceil(data.total / currentItemsPerPage);
          const startItem = (currentPage - 1) * currentItemsPerPage + 1;
          const endItem = Math.min(currentPage * currentItemsPerPage, data.total);
          setPagination({
            rangeRows: `${startItem}-${endItem} of ${data.total}`,
            totalPages,
            isTheLastPage: currentPage >= totalPages,
          });
          if (!isFirstLoadComplete) {
            setIsFirstLoadComplete(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch orders.', error);
      } finally {
        if (isMounted && showLoader) {
          setIsLoading(false);
        }
      }
    };

    if (currentPage === 1 && shouldSkipRequest()) {
      const params = new URLSearchParams();
      selectedChannels.forEach((c) => params.append('channel', c));
      selectedStatuses.forEach((s) => params.append('statuses', s));
      if (searchQuery) params.set('search', searchQuery);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (dateField) params.set('dateField', dateField);
      if (sortKey !== 'date_of_order') params.set('sortKey', sortKey);
      if (sortDir !== 'desc') params.set('sortDir', sortDir);
      params.set('page', currentPage);
      params.set('itemsPerPage', currentItemsPerPage);
      setSearchParams(params, { replace: true });
      return;
    }

    fetchOrders(true);
    const intervalId = setInterval(() => fetchOrders(false), 10000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [selectedChannels, selectedStatuses, startDate, endDate, currentPage, currentItemsPerPage, searchQuery, dateField, sortKey, sortDir]);

  useEffect(() => {
    const params = new URLSearchParams();
    selectedChannels.forEach((c) => params.append('channel', c));
    selectedStatuses.forEach((s) => params.append('statuses', s));
    if (searchQuery) params.set('search', searchQuery);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (dateField) params.set('dateField', dateField);
    if (sortKey !== 'date_of_order') params.set('sortKey', sortKey);
    if (sortDir !== 'desc') params.set('sortDir', sortDir);
    params.set('page', currentPage);
    params.set('itemsPerPage', currentItemsPerPage);
    setSearchParams(params, { replace: true });
  }, [selectedChannels, selectedStatuses, searchQuery, startDate, endDate, dateField, sortKey, sortDir, currentPage, currentItemsPerPage]);

  const clearFilters = () => {
    setCurrentPage(1);
    setSelectedChannels(['Shopify', 'Amazon']);
    setSelectedStatuses([...STATUS_VALUES]);
    setStartDate('');
    setEndDate('');
    setDateField('date_of_order');
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchQuery(searchTerm.trim());
  };

  const handleExportCSV = async (type) => {
    const params = buildRequestParams();
    params.append('type', type);

    const response = await fetch(`/api/v1/orders/export?${params.toString()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.error || 'Export failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `orders_export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  };

  const handleExportSelected = () => {
    const selectedSet = new Set(selectedOrderIds);
    const selected = orders.filter((o) => selectedSet.has(o.id));
    if (selected.length === 0) return;
    const cols = ALL_COLUMNS.filter(
      (c) => c.key !== 'items' && (c.pinned || visibleColumns.includes(c.key))
    );
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const header = cols.map((c) => escapeCsv(c.label)).join(',');
    const rows = selected.map((order) =>
      cols
        .map((c) => {
          switch (c.key) {
            case 'customer':
              return escapeCsv(order.customer_email);
            case 'margin': {
              const margin = computeMargin(order);
              return escapeCsv(margin != null ? margin.toFixed(1) : '');
            }
            case 'total_paid':
              return escapeCsv(order.display_total_paid ?? order.total_paid);
            default:
              return escapeCsv(order[c.key]);
          }
        })
        .join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `orders_selected_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const persistSavedViews = (next) => {
    setSavedViews(next);
    try {
      localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage failures
    }
  };

  const handleSaveView = (name) => {
    const id = `custom-${Date.now().toString(36)}`;
    persistSavedViews([...savedViews, { id, name, columns: [...visibleColumns] }]);
    setActiveViewId(id);
    toast.success(`View "${name}" saved`, { position: 'bottom-right' });
  };

  const handleUpdateView = (id) => {
    const view = savedViews.find((v) => v.id === id);
    if (!view) return;
    persistSavedViews(
      savedViews.map((v) => (v.id === id ? { ...v, columns: [...visibleColumns] } : v))
    );
    toast.success(`View "${view.name}" updated`, { position: 'bottom-right' });
  };

  const handleDeleteView = (id) => {
    const view = savedViews.find((v) => v.id === id);
    persistSavedViews(savedViews.filter((v) => v.id !== id));
    if (activeViewId === id) {
      setActiveViewId('default');
      setVisibleColumns([...DEFAULT_VIEW_COLUMNS]);
    }
    if (view) toast.success(`View "${view.name}" deleted`, { position: 'bottom-right' });
  };

  const allViews = useMemo(
    () => [
      ...VIEW_PRESETS.map((v) => ({ id: v.key, name: v.label, columns: v.columns })),
      ...savedViews,
    ],
    [savedViews]
  );

  const activeView = allViews.find((v) => v.id === activeViewId) || allViews[0];
  const activeSavedView = savedViews.find((v) => v.id === activeViewId) || null;
  const isViewDirty = useMemo(() => {
    if (!activeView) return false;
    if (activeView.columns.length !== visibleColumns.length) return true;
    const set = new Set(activeView.columns);
    return visibleColumns.some((k) => !set.has(k));
  }, [activeView, visibleColumns]);

  const selectView = (id) => {
    const view = allViews.find((v) => v.id === id);
    if (!view) return;
    setActiveViewId(id);
    setVisibleColumns([...view.columns]);
  };

  const toggleSelectOrder = (id) => {
    setSelectedOrderIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allPageSelected = orders.length > 0 && orders.every((o) => selectedOrderIds.includes(o.id));
  const toggleSelectAll = () => {
    if (allPageSelected) {
      const pageIds = new Set(orders.map((o) => o.id));
      setSelectedOrderIds((prev) => prev.filter((id) => !pageIds.has(id)));
    } else {
      const pageIds = orders.map((o) => o.id);
      setSelectedOrderIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const hasActiveFilters =
    selectedChannels.length < CHANNEL_OPTIONS.length ||
    selectedStatuses.length < STATUS_VALUES.length ||
    startDate ||
    endDate ||
    dateField !== 'date_of_order';

  const channelActive =
    dateField !== 'date_of_order' ||
    (selectedChannels.length > 0 && selectedChannels.length < CHANNEL_OPTIONS.length);
  const channelLabel =
    dateField !== 'date_of_order'
      ? 'Amazon'
      : selectedChannels.length === CHANNEL_OPTIONS.length || selectedChannels.length === 0
        ? 'Channel'
        : selectedChannels.join(', ');

  const statusActive = selectedStatuses.length < STATUS_VALUES.length;
  const statusLabel = statusActive ? `Status \u00b7 ${selectedStatuses.length}` : 'Status';

  const dateActive = Boolean(startDate || endDate);
  const dateLabel = !dateActive
    ? 'Date'
    : datePreset === 'custom'
      ? startDate && endDate
        ? `${formatShortRangeDate(startDate)} \u2013 ${formatShortRangeDate(endDate)}`
        : 'Custom'
      : DATE_PRESETS.find((p) => p.key === datePreset)?.label || 'Date';

  const isColumnVisible = (key) =>
    key === 'external_order_id' || visibleColumns.includes(key);

  const detailsOrder = detailsOrderId
    ? orders.find((o) => o.id === detailsOrderId) || null
    : null;

  const visibleColumnDefs = ALL_COLUMNS.filter((c) => isColumnVisible(c.key));

  const handleSort = (key) => {
    setCurrentPage(1);
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const renderCell = (order, key) => {
    switch (key) {
      case 'external_order_id':
        return (
          <span
            className="font-medium tabular-nums text-gray-900"
            title={`#${order.external_order_id}`}
          >
            #{String(order.external_order_id).slice(-8)}
          </span>
        );
      case 'date_of_order':
        return <span className="tabular-nums text-gray-500">{formatOrderDate(order.date_of_order)}</span>;
      case 'channel':
        return <ChannelPill channel={order.channel} externalOrderId={order.external_order_id} />;
      case 'order_status':
        return <StatusCell status={order.order_status} channel={order.channel} />;
      case 'items':
        return <ItemsCell order={order} />;
      case 'customer':
        return order.customer_email ? (
          <span className="text-gray-500">{order.customer_email}</span>
        ) : (
          <span className="text-gray-300">&mdash;</span>
        );
      case 'gross_sales':
        return <MoneyCell value={order.gross_sales} />;
      case 'discounts':
        return <MoneyCell value={order.discounts} negativeRed dashZero />;
      case 'taxes':
        return <MoneyCell value={order.taxes} />;
      case 'shipping_charges':
        return <MoneyCell value={order.shipping_charges} dashZero />;
      case 'refund_total':
        return <MoneyCell value={order.refund_total} negativeRed dashZero />;
      case 'total_paid':
        return <MoneyCell value={order.display_total_paid ?? order.total_paid} bold />;
      case 'landed_cost':
        return <MoneyCell value={order.landed_cost} dashZero />;
      case 'weighted_cost':
        return <MoneyCell value={order.weighted_cost} dashZero />;
      case 'margin': {
        const margin = computeMargin(order);
        return margin != null ? (
          <span className={`tabular-nums ${marginToneClass(margin)}`}>{margin.toFixed(1)}%</span>
        ) : (
          <span className="text-gray-300">&mdash;</span>
        );
      }
      case 'box_name':
        return order.box_name ? (
          <span className="text-gray-700">{order.box_name}</span>
        ) : (
          <span className="text-gray-300">&mdash;</span>
        );
      case 'earliest_ship_date':
        return <span className="text-gray-700">{formatOrderDate(order.earliest_ship_date)}</span>;
      case 'latest_ship_date':
        return <span className="text-gray-700">{formatOrderDate(order.latest_ship_date)}</span>;
      case 'utm_source':
      case 'utm_medium':
      case 'utm_campaign':
      case 'referring_site':
        return order[key] ? (
          <span className="text-gray-500 truncate max-w-[180px] inline-block align-bottom">{order[key]}</span>
        ) : (
          <span className="text-gray-300">&mdash;</span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Header */}
      <div className="px-4 sm:px-6 h-14 border-b border-gray-200 flex items-center gap-3">
        <h1 className="text-xl font-bold tracking-tight text-gray-900">Orders</h1>
        <span className="hidden sm:inline text-xs text-gray-500">
          Shopify + Amazon order ledger &middot; synced from Eadin
        </span>
      </div>

      {/* Toolbar */}
      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-4 py-2">
          <div className="relative w-full sm:w-64">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders&hellip;"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              className="h-8 w-full rounded-md border border-gray-300 bg-white pl-8 pr-8 text-[13px] outline-none placeholder:text-gray-400 focus:ring-1 focus:ring-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                  setSearchQuery('');
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Filter pills - horizontally scrollable on mobile */}
          <div className="-mx-4 flex w-[calc(100%+2rem)] items-center gap-2 overflow-x-auto px-4 pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] sm:mx-0 sm:w-auto sm:overflow-visible sm:p-0 [&::-webkit-scrollbar]:hidden">
            <FilterPill
              label={channelLabel}
              active={channelActive}
              onClear={
                dateField !== 'date_of_order'
                  ? null
                  : () => {
                      setCurrentPage(1);
                      setSelectedChannels([...CHANNEL_OPTIONS]);
                    }
              }
              open={showChannelPopover}
              setOpen={setShowChannelPopover}
            >
              <div className="w-40 py-1">
                {[
                  { label: 'All channels', value: [...CHANNEL_OPTIONS] },
                  { label: 'Shopify', value: ['Shopify'] },
                  { label: 'Amazon', value: ['Amazon'] },
                ].map((opt) => {
                  const isSelected =
                    opt.value.length === selectedChannels.length &&
                    opt.value.every((c) => selectedChannels.includes(c));
                  return (
                    <button
                      key={opt.label}
                      disabled={dateField !== 'date_of_order'}
                      onClick={() => {
                        setCurrentPage(1);
                        setSelectedChannels(opt.value);
                        setShowChannelPopover(false);
                      }}
                      className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[13px] text-gray-800 hover:bg-gray-50 disabled:text-gray-300"
                    >
                      {opt.label}
                      {isSelected && <CheckIcon className="h-3.5 w-3.5" />}
                    </button>
                  );
                })}
              </div>
            </FilterPill>

            <FilterPill
              label={statusLabel}
              active={statusActive}
              onClear={() => {
                setCurrentPage(1);
                setSelectedStatuses([...STATUS_VALUES]);
              }}
              open={showStatusPopover}
              setOpen={setShowStatusPopover}
            >
              <StatusFilterPanel
                groups={STATUS_GROUPS}
                allValues={STATUS_VALUES}
                selected={selectedStatuses}
                onChange={(statuses) => {
                  setCurrentPage(1);
                  setSelectedStatuses(statuses);
                }}
              />
            </FilterPill>

            <FilterPill
              label={dateLabel}
              active={dateActive}
              onClear={() => {
                setCurrentPage(1);
                setStartDate('');
                setEndDate('');
              }}
              open={showDatePopover}
              setOpen={setShowDatePopover}
            >
              <div className="w-44 py-1">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    onClick={() => {
                      const dates = getPresetDates(preset.key);
                      setCurrentPage(1);
                      setStartDate(dates.startDate);
                      setEndDate(dates.endDate);
                      setShowDatePopover(false);
                    }}
                    className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[13px] text-gray-800 hover:bg-gray-50"
                  >
                    {preset.label}
                    {datePreset === preset.key && <CheckIcon className="h-3.5 w-3.5" />}
                  </button>
                ))}
                <div className="mx-2 my-1 border-t border-gray-200" />
                <button
                  onClick={() => {
                    setShowDatePopover(false);
                    setShowCalendar(true);
                  }}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[13px] text-gray-800 hover:bg-gray-50"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5 text-gray-400" />
                    Custom
                  </span>
                  {datePreset === 'custom' && dateActive && <CheckIcon className="h-3.5 w-3.5" />}
                </button>
              </div>
            </FilterPill>

            {/* Date field (Amazon shipping dates) */}
            <select
              value={dateField}
              onChange={(e) => {
                setCurrentPage(1);
                setDateField(e.target.value);
                if (e.target.value !== 'date_of_order') {
                  setSelectedChannels(['Amazon']);
                }
              }}
              className="h-8 flex-shrink-0 rounded-md border border-gray-300 bg-white px-2 text-[12px] text-gray-500"
              title="Which date field the range filters on"
            >
              <option value="date_of_order">Order Date</option>
              <option value="earliest_ship_date">Earliest Ship</option>
              <option value="latest_ship_date">Latest Ship</option>
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex-shrink-0 text-xs text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline"
              >
                Clear all
              </button>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden text-xs tabular-nums text-gray-500 sm:inline whitespace-nowrap">
              {totalRecords.toLocaleString()} orders
            </span>

            <div className="hidden items-center gap-2 sm:flex">
              {/* View preset dropdown */}
              <div className="relative" ref={viewDropdownRef}>
                <button
                  onClick={() => setShowViewDropdown((v) => !v)}
                  className="inline-flex h-8 w-[150px] items-center justify-between gap-1 rounded-md border border-gray-300 bg-white px-2.5 text-[13px] text-gray-800 hover:bg-gray-50"
                >
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="truncate">{activeView?.name || 'Default View'}</span>
                    {isViewDirty && (
                      <span
                        className="flex-shrink-0 text-gray-500"
                        title="Columns differ from the saved view"
                      >
                        &bull;
                      </span>
                    )}
                  </span>
                  <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                </button>
                {showViewDropdown && (
                  <div className="absolute right-0 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg z-40">
                    {VIEW_PRESETS.map((view) => (
                      <button
                        key={view.key}
                        onClick={() => {
                          selectView(view.key);
                          setShowViewDropdown(false);
                        }}
                        className={`block w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 ${
                          activeViewId === view.key ? 'font-semibold text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {view.label}
                      </button>
                    ))}
                    {savedViews.length > 0 && <div className="mx-1 my-1 border-t border-gray-200" />}
                    {savedViews.map((view) => (
                      <button
                        key={view.id}
                        onClick={() => {
                          selectView(view.id);
                          setShowViewDropdown(false);
                        }}
                        className={`block w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50 ${
                          activeViewId === view.id ? 'font-semibold text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {view.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowColumnsPanel(true)}
                className={`inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-300 px-2.5 text-[13px] text-gray-800 hover:bg-gray-50 ${
                  showColumnsPanel ? 'bg-gray-100' : 'bg-white'
                }`}
              >
                <ViewColumnsIcon className="h-3.5 w-3.5" />
                Columns
              </button>

              <CSVExportDropdown onExport={handleExportCSV} disabled={isLoading} />
            </div>
          </div>
        </div>

        {/* Custom date range calendar popover */}
        {showCalendar && (
          <div
            ref={calendarRef}
            className="absolute left-2 top-full z-40 mt-1 max-h-[80vh] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
          >
            <CustomDateCalendar
              range={{ from: startDate || null, to: endDate || null }}
              onChange={({ from, to }) => {
                setCurrentPage(1);
                setStartDate(from || '');
                setEndDate(to || '');
              }}
              onComplete={() => setShowCalendar(false)}
            />
          </div>
        )}

        {/* Bulk edit bar */}
        {selectedOrderIds.length > 0 && (
          <div className="flex h-10 items-center gap-3 bg-gray-900 px-4 text-white">
            <button
              onClick={() => setSelectedOrderIds([])}
              className="flex h-5 w-5 items-center justify-center rounded hover:bg-white/15"
              title="Clear selection"
              aria-label="Clear selection"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
            <span className="text-[13px] font-medium tabular-nums">
              {selectedOrderIds.length} selected
            </span>
            <div className="mx-1 h-4 w-px bg-white/25" />
            <button
              onClick={handleExportSelected}
              className="rounded px-2 py-1 text-[13px] hover:bg-white/15"
            >
              Export selected
            </button>
          </div>
        )}
      </div>

      {/* Mobile card list */}
      <div className="relative flex-1 sm:hidden">
        {isLoading && isFirstLoadComplete && (
          <>
            <OverlayGrayBackground />
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <LoaderSpinner width="70" height="70" border="3" colorSpinner="black" />
            </div>
          </>
        )}
        {isFirstLoadComplete && (
          <OrdersCardList
            orders={orders}
            selectedIds={selectedOrderIds}
            onToggleSelect={toggleSelectOrder}
            onOpen={setDetailsOrderId}
          />
        )}
      </div>

      {/* Table */}
      <div className="relative flex-1 overflow-x-auto hidden sm:block">
        {isLoading && isFirstLoadComplete && (
          <>
            <OverlayGrayBackground />
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <LoaderSpinner width="70" height="70" border="3" colorSpinner="black" />
            </div>
          </>
        )}
        <table className="min-w-full border-collapse bg-white text-[13px]">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr>
              <th
                className="w-10 bg-gray-50 pl-4 pr-2 py-2 text-left"
                style={{ boxShadow: '0 1px 0 0 #e5e7eb' }}
              >
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        !allPageSelected && orders.some((o) => selectedOrderIds.includes(o.id));
                    }
                  }}
                  onChange={toggleSelectAll}
                  className="h-3.5 w-3.5 accent-gray-900 rounded align-middle"
                  aria-label="Select all orders on page"
                />
              </th>
              {visibleColumnDefs.map((col) => {
                const sortable = SORTABLE_COLUMNS[col.key];
                const isSorted = sortable && sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => handleSort(col.key) : undefined}
                    className={`bg-gray-50 px-3 py-2 text-[11px] font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                      RIGHT_ALIGNED_COLUMNS.includes(col.key) ? 'text-right' : 'text-left'
                    } ${sortable ? 'cursor-pointer select-none hover:text-gray-900' : ''}`}
                    style={{ boxShadow: '0 1px 0 0 #e5e7eb' }}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {isSorted &&
                        (sortDir === 'desc' ? (
                          <ArrowDownIcon className="h-3 w-3" />
                        ) : (
                          <ArrowUpIcon className="h-3 w-3" />
                        ))}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const isSelected = selectedOrderIds.includes(order.id);
              return (
                <tr
                  key={order.id}
                  className={`border-b border-gray-100 cursor-pointer transition-colors ${
                    isSelected || detailsOrderId === order.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setDetailsOrderId(order.id)}
                >
                  <td className="w-10 pl-4 pr-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectOrder(order.id)}
                      className="h-3.5 w-3.5 accent-gray-900 rounded align-middle"
                      aria-label={`Select order ${order.external_order_id}`}
                    />
                  </td>
                  {visibleColumnDefs.map((col) => (
                    <td
                      key={col.key}
                      className={`max-w-[280px] truncate whitespace-nowrap px-3 py-2 ${
                        RIGHT_ALIGNED_COLUMNS.includes(col.key) ? 'text-right' : 'text-left'
                      }`}
                    >
                      {renderCell(order, col.key)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {isFirstLoadComplete && !isLoading && orders.length === 0 && (
              <tr>
                <td
                  colSpan={visibleColumnDefs.length + 1}
                  className="px-3 py-16 text-center text-gray-400"
                >
                  No orders match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isFirstLoadComplete && (
        <div className="z-10 h-60">
          <LoaderSpinner width="70" height="70" border="3" colorSpinner="black" />
        </div>
      )}

      {orders.length > 0 && (
        <div className="px-4 sm:px-6">
          <Pagination
            currentItemsPerPage={currentItemsPerPage}
            rangeRows={pagination.rangeRows}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isTheLastPage={pagination.isTheLastPage}
            totalPages={pagination.totalPages}
            setCurrentItemsPerPage={setCurrentItemsPerPage}
          />
        </div>
      )}

      {showColumnsPanel && (
        <ColumnsPanel
          visibleColumns={visibleColumns}
          onChange={setVisibleColumns}
          savedViews={savedViews}
          activeSavedView={activeSavedView}
          isDirty={isViewDirty}
          onSaveView={handleSaveView}
          onUpdateView={handleUpdateView}
          onDeleteView={handleDeleteView}
          onClose={() => setShowColumnsPanel(false)}
        />
      )}

      {!showColumnsPanel && detailsOrder && (
        <OrderDetailsDrawer order={detailsOrder} onClose={() => setDetailsOrderId(null)} />
      )}
    </div>
  );
};

export default Orders;
